import { Bar, BarChart, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

const PALETTE = ["#83B68B", "#60A5FA", "#F59E0B", "#F472B6", "#A78BFA", "#34D399", "#FB7185", "#FBBF24"];

export function Delta({ value, prev, bad }) {
  if (value == null || prev == null) return null;
  const diff = value - prev;
  const good = bad ? diff <= 0 : diff >= 0;
  const Icon = diff === 0 ? Minus : diff > 0 ? ArrowUp : ArrowDown;
  return (
    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${diff === 0 ? "border-border opacity-60" : good ? "border-[#83B68B]/50 text-[#83B68B] bg-[#83B68B]/10" : "border-red-400/50 text-red-300 bg-red-500/10"}`} data-testid="committee-delta">
      <Icon className="w-3 h-3" /> {diff > 0 ? "+" : ""}{Math.round(diff * 10) / 10} vs prev
    </span>
  );
}

export function Headline({ label, value, prev, bad, suffix = "" }) {
  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div><div className="font-display font-black text-4xl text-[#83B68B] leading-none" data-testid="committee-headline">{value ?? "—"}{value != null ? suffix : ""}</div><div className="text-[10px] tracking-widest uppercase opacity-60 mt-1">{label}</div></div>
      <Delta value={value} prev={prev} bad={bad} />
    </div>
  );
}

export function Stat({ label, value, suffix = "" }) {
  return <div className="rounded border border-border px-3 py-2"><div className="font-display font-bold text-xl">{value ?? "—"}{value != null ? suffix : ""}</div><div className="text-[10px] tracking-widest uppercase opacity-60">{label}</div></div>;
}

export function MiniBar({ data, height = 140, color = "#83B68B" }) {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "currentColor", opacity: 0.6 }} interval={0} tickFormatter={v => String(v).slice(0, 10)} />
        <YAxis tick={{ fontSize: 10, fill: "currentColor", opacity: 0.6 }} allowDecimals={false} />
        <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function MiniLine({ data, height = 140 }) {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "currentColor", opacity: 0.6 }} />
        <YAxis tick={{ fontSize: 10, fill: "currentColor", opacity: 0.6 }} />
        <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
        <Line type="monotone" dataKey="value" stroke="#83B68B" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, height = 140 }) {
  if (!data?.length) return <Empty />;
  return (
    <div className="flex items-center gap-3">
      <ResponsiveContainer width={height} height={height}>
        <PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={height / 3.2} outerRadius={height / 2.2} stroke="none">{data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} /></PieChart>
      </ResponsiveContainer>
      <ul className="text-xs space-y-1">{data.slice(0, 6).map((d, i) => <li key={d.name} className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} /> {d.name} <span className="opacity-60">{d.value}</span></li>)}</ul>
    </div>
  );
}

export function Empty({ text = "Nothing recorded for this period" }) {
  return <div className="text-xs opacity-50 py-6 text-center border border-dashed border-border rounded" data-testid="committee-empty">{text}</div>;
}
